import axios from 'axios';
import React, { useCallback, useRef, useState } from 'react';

import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, Grid, Typography } from '@mui/material';

interface AddressSuggestion {
  text: string;
  placeId: string;
}

export interface AddressDetails {
  address: string;
  locality: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  placeId: string;
  lat: number | null;
  lng: number | null;
}

export interface LocationAutocompleteProps {
  city: string;
  state: string;
  country: string;
  locality?: string;
  zipCode?: string;
  address?: string;
  disabled?: boolean;
  /** Marks the address search, which is the only writer of city / state / country. */
  required?: boolean;
  /**
   * Validation message for the resolved address as a whole.
   *
   * There is no manual City / State / Country input here — the only writer is a suggestion pick —
   * so a caller whose validator requires those fields has nowhere to put the failure. `/jobs/new`
   * computed `city`/`state`/`country` errors and threw them away, which is how Save came to do
   * nothing at all with no message on screen.
   */
  error?: string;
  /** Called for individual City / State / Country field changes (kept for back-compat) */
  onChange: (name: 'city' | 'state' | 'country', value: string) => void;
  /** Called with full structured details when a Places suggestion is selected */
  onAddressChange?: (details: AddressDetails) => void;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  city,
  state,
  country,
  locality = '',
  zipCode = '',
  address = '',
  disabled = false,
  required = false,
  error,
  onChange,
  onAddressChange,
}) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [addressInput, setAddressInput] = useState<string>(address);
  const [searchLoading, setSearchLoading] = useState(false);
  /* Both lookups used to `catch {}` into an empty dropdown, so a caller could not tell "nothing
     matched" from "the geocoding service is down" — and the second reads as the first. */
  const [lookupFailed, setLookupFailed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddresses = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) { setSuggestions([]); return; }
    setSearchLoading(true);
    try {
      const response = await axios.get('/api/addressSearch', { params: { query: query.trim() } });
      setSuggestions(response.data ?? []);
      setLookupFailed(false);
    } catch {
      setSuggestions([]);
      setLookupFailed(true);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleAddressInputChange = useCallback(
    (_: any, inputVal: string) => {
      setAddressInput(inputVal);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchAddresses(inputVal), 300);
    },
    [searchAddresses],
  );

  const handleAddressSelect = useCallback(
    async (_: any, selected: AddressSuggestion | string | null) => {
      if (!selected || typeof selected === 'string') return;
      setAddressInput(selected.text);
      try {
        const response = await axios.get('/api/placeDetails', { params: { placeId: selected.placeId } });
        const details = response.data;
        onChange('city', details.city ?? '');
        onChange('state', details.state ?? '');
        onChange('country', details.country ?? '');
        if (onAddressChange) {
          onAddressChange({
            address: details.formattedAddress ?? selected.text,
            locality: details.locality ?? '',
            city: details.city ?? '',
            state: details.state ?? '',
            country: details.country ?? '',
            zipCode: details.zipCode ?? '',
            placeId: selected.placeId,
            lat: details.lat ?? null,
            lng: details.lng ?? null,
          });
        }
        setLookupFailed(false);
      } catch {
        // Values are left untouched on purpose, but the user has to be told: without this the
        // address looks accepted while city/state/country stay empty, and the form blocks later
        // with no visible cause.
        setLookupFailed(true);
      }
    },
    [onChange, onAddressChange],
  );

  // Build deduplicated address parts for read-only display.
  const addressParts = [locality, city, state, country, zipCode]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .filter((part, index, arr) => arr.findIndex((p) => p.toLowerCase() === part.toLowerCase()) === index);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <AutocompleteFormField
          freeSolo
          fullWidth
          required={required}
          label="Search locality or city"
          name="addressSearch"
          inputValue={addressInput}
          value={null}
          options={suggestions}
          loading={searchLoading}
          disabled={disabled}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.text)}
          isOptionEqualToValue={(opt, val) => {
            const a = typeof opt === 'string' ? opt : opt.placeId;
            const b = typeof val === 'string' ? val : val.placeId;
            return a === b;
          }}
          autocompleteProps={{
            renderOption: (props: any, opt: AddressSuggestion) => (
              <li {...props} key={opt.placeId}><span>{opt.text}</span></li>
            ),
          }}
          error={!!error}
          helperText={error}
          onChange={handleAddressSelect}
          onInputChange={handleAddressInputChange}
        />
        {lookupFailed && !error && (
          <Typography variant="body2" sx={{ mt: 0.5, color: 'var(--red)' }}>
            Address lookup is unavailable right now. Try again in a moment.
          </Typography>
        )}
      </Grid>

      {/* Read-only address pill — shown once any address part is present */}
      {addressParts.length > 0 && (
        <Grid size={12}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 1.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}>
            <LocationOnOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled', flexShrink: 0 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
              {addressParts.map((val, i, arr) => (
                <React.Fragment key={i}>
                  <Typography variant="body2" sx={{ fontWeight: i === 0 ? 600 : 400, color: i === 0 ? 'text.primary' : 'text.secondary' }}>
                    {val}
                  </Typography>
                  {i < arr.length - 1 && (
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>·</Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default LocationAutocomplete;
